import type { HarmonyPackage, HarmonyPackageBoard, HarmonyPackageDocument, HarmonyWorkitem } from '@rhythmic/db/types'
import type { HarmonyPackageFolder, TeamType } from '@rhythmic/types'
import type { Collection, UseLiveQueryReturn } from '@tanstack/vue-db'
import { createHooks } from 'hookable'
import type { Ref } from 'vue'
import { computed, markRaw, shallowRef, watch } from 'vue'
import { HARMONY_PACKAGE_TABLE } from '../../collections'
import { useCollection } from '../../composables/useCollection'
import type { HarmonyItem } from '../../queries'
import {
  useBoardsByHarmonyPlanId,
  useDocumentsByHarmonyPlanId,
  useFoldersByHarmonyPlanId,
  useHarmonyItems,
} from '../../queries'
import useHarmonyPlanContextOnContextChanged from './events/useHarmonyPlanContext.onContextChanged'
import useHarmonyPlanContextOnPlanIdChanged from './events/useHarmonyPlanContext.onPlanIdChanged'
import type { HarmonyPlanContextEvents } from './useHarmonyPlanContext.events.schema'

interface HarmonyPlanContextLoggerLike {
  debug: (message: string, meta?: Record<string, unknown> | unknown) => void
  warn: (message: string, meta?: Record<string, unknown> | unknown) => void
  error: (message: string, meta?: Record<string, unknown> | unknown) => void
}

interface InitHarmonyPlanContextQueriesParams {
  logger: HarmonyPlanContextLoggerLike
  harmonyPlanId: Readonly<Ref<string | undefined>>
  harmonyPlan: Readonly<Ref<HarmonyPackage | null | undefined>>
  team: Readonly<Ref<TeamType | undefined>>
  workspaceId: Readonly<Ref<string | undefined>>
}

/**
 * Harmony plan context query orchestrator with explicit, hook-driven updates.
 *
 * Route params are synced into `harmonyPlanId` by `harmony-plan-context-router.client`.
 * The resolved plan/team/workspace identifiers are observed and emitted as a `ContextChanged`
 * hookable event, which handlers use to (re)configure live queries and backing collections.
 */
export default function useHarmonyPlanContextQueries() {
  type HarmonyPlanContextHookMap = {
    [K in keyof HarmonyPlanContextEvents]: (event: HarmonyPlanContextEvents[K]) => void | Promise<void>
  }

  const hooks = markRaw(createHooks<HarmonyPlanContextHookMap>())

  const harmonyDocumentsQuery = shallowRef<UseLiveQueryReturn<HarmonyPackageDocument> | undefined>(undefined)
  const harmonyBoardsQuery = shallowRef<UseLiveQueryReturn<HarmonyPackageBoard> | undefined>(undefined)
  const harmonyFoldersQuery = shallowRef<UseLiveQueryReturn<HarmonyPackageFolder> | undefined>(undefined)
  const harmonyWorkitemQuery = shallowRef<UseLiveQueryReturn<HarmonyItem> | undefined>(undefined)

  const harmonyPackageCollection = shallowRef<Collection<HarmonyPackage> | undefined>(undefined)
  const harmonyWorkitemCollection = shallowRef<Collection<HarmonyWorkitem> | undefined>(undefined)
  const harmonyBoardCollection = shallowRef<Collection<HarmonyPackageBoard> | undefined>(undefined)
  const harmonyDocumentCollection = shallowRef<Collection<HarmonyPackageDocument> | undefined>(undefined)

  const documents = computed<HarmonyPackageDocument[]>(() => harmonyDocumentsQuery.value?.data.value ?? [])
  const boards = computed<HarmonyPackageBoard[]>(() => harmonyBoardsQuery.value?.data.value ?? [])
  const folders = computed<HarmonyPackageFolder[]>(() => harmonyFoldersQuery.value?.data.value ?? [])
  const harmonyWorkitems = computed<HarmonyItem[]>(() => harmonyWorkitemQuery.value?.data.value ?? [])

  const isDocumentLoading = computed<boolean>(() => harmonyDocumentsQuery.value?.isLoading.value ?? true)
  const isBoardLoading = computed<boolean>(() => harmonyBoardsQuery.value?.isLoading.value ?? true)
  const isFolderLoading = computed<boolean>(() => harmonyFoldersQuery.value?.isLoading.value ?? true)
  const isWorkitemLoading = computed<boolean>(() => harmonyWorkitemQuery.value?.isLoading.value ?? true)

  const isLoading = computed<boolean>(() => {
    return isDocumentLoading.value || isBoardLoading.value || isFolderLoading.value || isWorkitemLoading.value
  })

  let hasInitialized = false

  function clearPlanScopedQueries() {
    harmonyDocumentsQuery.value = undefined
    harmonyBoardsQuery.value = undefined
    harmonyFoldersQuery.value = undefined
    harmonyWorkitemQuery.value = undefined
    harmonyBoardCollection.value = undefined
    harmonyDocumentCollection.value = undefined
    harmonyPackageCollection.value = undefined
    harmonyWorkitemCollection.value = undefined
  }

  function init({ logger, harmonyPlanId, harmonyPlan, team, workspaceId }: InitHarmonyPlanContextQueriesParams) {
    if (hasInitialized) return

    hasInitialized = true

    useHarmonyPlanContextOnPlanIdChanged({ hooks, logger })

    useHarmonyPlanContextOnContextChanged({
      hooks,
      logger,
      harmonyDocumentsQuery,
      harmonyBoardsQuery,
      harmonyFoldersQuery,
      harmonyWorkitemQuery,
      harmonyPackageCollection,
      harmonyWorkitemCollection,
      harmonyBoardCollection,
      harmonyDocumentCollection,
      clearPlanScopedQueries,
      factories: {
        createDocumentsQuery: (planId) => useDocumentsByHarmonyPlanId(planId),
        createBoardsQuery: (planId) => useBoardsByHarmonyPlanId(planId),
        createFoldersQuery: (planId) => useFoldersByHarmonyPlanId(planId),
        createWorkitemsQuery: (planId, teamId) => useHarmonyItems(planId, teamId),
        createHarmonyPlanCollection: (planId, teamId, workspaceId) =>
          useCollection(HARMONY_PACKAGE_TABLE, {
            team_id: teamId,
            workspace_id: workspaceId,
            id: planId,
          }).collection as Collection<HarmonyPackage>,
      },
    })

    const resolvedPlanId = computed<string | undefined>(() => harmonyPlan.value?.id ?? harmonyPlanId.value)
    const resolvedTeamId = computed<string | undefined>(() => team.value?.id ?? undefined)
    const resolvedWorkspaceId = computed<string | undefined>(
      () => workspaceId.value ?? team.value?.workspace_id ?? undefined,
    )

    watch(
      [resolvedPlanId, resolvedTeamId, resolvedWorkspaceId],
      ([nextPlanId, nextTeamId, nextWorkspaceId], [previousPlanId, previousTeamId, previousWorkspaceId]) => {
        void emitContextChanged({
          harmonyPlanId: nextPlanId,
          previousHarmonyPlanId: previousPlanId,
          teamId: nextTeamId,
          previousTeamId,
          workspaceId: nextWorkspaceId,
          previousWorkspaceId,
        })
      },
      { immediate: true },
    )
  }

  function onPlanIdChanged(handler: HarmonyPlanContextHookMap['PlanIdChanged']) {
    return hooks.hook('PlanIdChanged', handler)
  }

  function onContextChanged(handler: HarmonyPlanContextHookMap['ContextChanged']) {
    return hooks.hook('ContextChanged', handler)
  }

  async function emitPlanIdChanged(event: HarmonyPlanContextEvents['PlanIdChanged']) {
    return hooks.callHook('PlanIdChanged', event)
  }

  async function emitContextChanged(event: HarmonyPlanContextEvents['ContextChanged']) {
    return hooks.callHook('ContextChanged', event)
  }

  return {
    init,
    hooks,
    onPlanIdChanged,
    onContextChanged,
    emitPlanIdChanged,
    emitContextChanged,
    harmonyDocumentsQuery,
    harmonyBoardsQuery,
    harmonyFoldersQuery,
    harmonyWorkitemQuery,
    harmonyPackageCollection,
    harmonyWorkitemCollection,
    harmonyBoardCollection,
    harmonyDocumentCollection,
    documents,
    boards,
    folders,
    harmonyWorkitems,
    isLoading,
    isDocumentLoading,
    isBoardLoading,
    isFolderLoading,
    isWorkitemLoading,
  }
}
